import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, RefreshCw, ArrowLeft, Layers } from 'lucide-react';
import { api } from '@/lib/api';
import { useMembership } from '@/hooks/useMembership';
import { DateSelector, ChecklistStats, PondEntryCard } from '@/components/entry';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { format } from 'date-fns';
import {
  normalizeChecklistResponse,
} from '@/types/entry';
import type { ChecklistResponse as ApiChecklistResponse } from '@/types/generated/openapi';

export const Route = createFileRoute('/_app/entry/overview')({
  component: EntryOverview,
});

function EntryOverview() {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const { membership, isLoading: membershipLoading } = useMembership();

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const {
    data: checklist,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['entry-checklist', dateStr, membership?.farmId],
    queryFn: async () => {
      const response = await api.get<{ data: ApiChecklistResponse }>(
        `/api/v1/farms/${membership?.farmId}/entry/checklist`,
        { params: { target_date: dateStr } }
      );
      return normalizeChecklistResponse(response.data);
    },
    enabled: !!membership?.farmId,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Assignment only orders the list (yours first); every pond can be entered.
  const assignedIds = membership?.role === 'technician' ? membership.assignedPondIds : null;
  const ponds = (checklist?.ponds || [])
    .map((pond) => ({ ...pond, isAssigned: !assignedIds || assignedIds.includes(pond.id) }))
    .sort((a, b) => Number(b.isAssigned) - Number(a.isAssigned));

  // A technician with no assignments yet (#158) counts every pond, like everyone else.
  const mine = ponds.filter((p) => p.isAssigned);
  const assignedPonds = mine.length > 0 ? mine : ponds;
  const completedCount = assignedPonds.filter(
    (p) => p.completion.water === 'complete' && p.completion.feeding === 'complete'
  ).length;

  if (membershipLoading || (isLoading && !checklist)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="p-4 rounded-full bg-red-50 mb-4">
          <ClipboardList className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to load checklist</h2>
        <p className="text-gray-500 mb-4 max-w-sm">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Link
            to="/entry"
            search={{ date: dateStr }}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary-50">
                <ClipboardList className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Pond Overview
                </h1>
                <p className="text-gray-500 text-sm mt-0.5">
                  View completion status and enter data per pond
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/entry"
            search={{ scope: 'all', date: dateStr }}
          >
            <Button variant="outline" size="sm">
              <Layers className="w-4 h-4 mr-1.5" />
              All Ponds
            </Button>
          </Link>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Date Selector */}
      <DateSelector
        value={selectedDate}
        onChange={setSelectedDate}
        maxDaysBack={membership?.entryWindowDays ?? 0}
      />

      {/* Stats */}
      <ChecklistStats
        totalAssigned={assignedPonds.length}
        completed={completedCount}
        totalPonds={ponds.length}
      />

      {/* Pond Grid */}
      {ponds.length === 0 ? (
        <div className="text-center py-12">
          <div className="p-4 rounded-full bg-gray-100 inline-block mb-4">
            <ClipboardList className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No active ponds</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            There are no ponds with active cycles to enter data for.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ponds.map((pond) => (
            <PondEntryCard
              key={pond.id}
              pond={pond}
              date={selectedDate}
              canEnter={!!pond.activeCycle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
